import { LayoutModule } from '@angular/cdk/layout';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { BrowserModule } from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ContextToolbarModule } from './context-toolbar/context-toolbar.module';
import { CoreModule } from './core/core.module';
import { HttpInterceptorClient } from './core/HttpClient/HttpInterceptorClient';
import { ApiBackoffInterceptor } from './core/HttpClient/api-backoff.interceptor';
import { DashboardModule } from './dashboard/dashboard.module';
import { ProjectSetupModule } from './project/project-setup.module';
import { OfflineDialogModule } from './shared/offline-dialog/offline-dialog.module';
import { OfflineToolbarModule } from './shared/offline-toolbar/offline-toolbar.module';
import { PipesModule } from './shared/pipes/pipes.module';
import { OfflineDownloadsOverview } from './side-navigation/offline-downloads-overview/offline-downloads-overview.module';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalRedirectComponent,
  MsalService,
} from '@azure/msal-angular';
import { InteractionType, IPublicClientApplication, PublicClientApplication } from '@azure/msal-browser';
import { HeaderModule } from './header';
import { ReportsModule } from './reports/reports.module';
import { env } from 'src/environments/env';
import { HealthComponent } from './health/health.component';
import { SupplierModule } from './supplier/supplier.module';
import { MatIconModule } from '@angular/material/icon';
import { VendorModule } from './vendor/vendor.module';
import { ReviewCommentsModule } from './cp-overview/document/review-comments/review-comments.module';
import { ProjectOverviewModule } from './project-overview/project-overview.module';
import { UserManualModule } from './shared/user-manual/user-manual.module';
import { VersionUpdateSnackbarComponent } from './shared/version-update-snackbar/version-update-snackbar.component';
import { CommonModule } from '@angular/common';
import { HeatTraceModule } from './heat-trace/heat-trace.module';
import { providePrimeNG } from 'primeng/config';
import AkerPreset from 'src/styles/aker-primeng-preset';
import { NoProjectAccessComponent } from './no-project-access/no-project-access.component';
import { ButtonModule } from 'primeng/button';
import { DialogService } from 'primeng/dynamicdialog';

export function initializeMsalFactory(msalInstance: IPublicClientApplication) {
  return () => msalInstance.initialize();
}

export const MSALGuardConfigFactory = (): MsalGuardConfiguration => ({
  interactionType: InteractionType.Redirect,
  authRequest: {
    scopes: ['user.read'],
  },
});

export const MSALInstanceFactory = (): IPublicClientApplication =>
  new PublicClientApplication({
    auth: {
      clientId: env.get().auth.clientId,
      authority: 'https://login.microsoftonline.com/26b749f6-8c72-44e3-bbde-ae3de07b4206',
      redirectUri: '/callback',
    },
    cache: {
      cacheLocation: 'localStorage',
    },
  });

export const MSALInterceptorConfigFactory = (): MsalInterceptorConfiguration => {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['user.read']);
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me/photo', ['user.read']);
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me/photos', ['user.read']);
  protectedResourceMap.set('https://graph.microsoft.com/beta/me/photo', ['user.read']);

  protectedResourceMap.set(env.get().auth.protectedResource, [env.get().auth.apiScope]);
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
};

@NgModule({
  declarations: [AppComponent, HealthComponent, VersionUpdateSnackbarComponent, NoProjectAccessComponent],
  bootstrap: [AppComponent, MsalRedirectComponent],
  imports: [
    AppRoutingModule,
    BrowserModule,
    CommonModule,
    ContextToolbarModule,
    CoreModule,
    LayoutModule,
    HeaderModule,
    MatSnackBarModule,
    MsalModule,
    OfflineDialogModule,
    OfflineToolbarModule,
    PipesModule,
    DashboardModule,
    ProjectSetupModule,
    OfflineDownloadsOverview,
    ReportsModule,
    SupplierModule,
    VendorModule,
    MatIconModule,
    UserManualModule,
    ReviewCommentsModule,
    ProjectOverviewModule,
    ButtonModule,
    HeatTraceModule,
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiBackoffInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpInterceptorClient,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeMsalFactory,
      deps: [MSAL_INSTANCE],
      multi: true,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    MsalService,
    MsalGuard,
    provideHttpClient(withInterceptorsFromDi()),
    providePrimeNG({
      theme: {
        preset: AkerPreset,
        options: { darkModeSelector: false },
      },
    }),
    DialogService,
  ],
})
export class AppModule {}
